import { Scene } from 'phaser';
import { AccidentalGlyphGraphic, type AccidentalSymbol } from './Note';
import type { ClefType } from './Types';

const TREBLE_SHARP_LINES = [0, 3, -1, 2, 5, 1, 4];
const TREBLE_FLAT_LINES = [4, 1, 5, 2, 6, 3, 7];
const BASS_SHARP_LINES = [2, 5, 1, 4, 7, 3, 6];
const BASS_FLAT_LINES = [6, 3, 7, 4, 8, 5, 9];
const GLYPH_HEIGHT_IN_STAFF_GAPS = 1.95;
const GLYPH_WIDTH_TO_HEIGHT = 0.42;
const GLYPH_ADVANCE_FACTOR = 0.82;

export const getKeySignatureWidth = (
  fifths: number,
  pitchLineGap: number,
  dpr: number = 1,
): number => {
  const count = Math.abs(fifths);
  if (count === 0) return 0;
  const glyphWidth = pitchLineGap * dpr * GLYPH_HEIGHT_IN_STAFF_GAPS * GLYPH_WIDTH_TO_HEIGHT;
  return glyphWidth + Math.max(0, count - 1) * glyphWidth * GLYPH_ADVANCE_FACTOR;
};

export default class KeySignatureGraphic extends Phaser.GameObjects.Container {
  private readonly glyphs: AccidentalGlyphGraphic[];
  private readonly symbol: AccidentalSymbol;

  constructor(
    scene: Scene,
    private readonly clef: ClefType,
    private readonly fifths: number,
    private notationColor: number,
  ) {
    super(scene, 0, 0);
    this.symbol = fifths > 0 ? '#' : 'b';
    this.glyphs = Array.from(
      { length: Math.abs(fifths) },
      () => new AccidentalGlyphGraphic(scene, this.symbol, notationColor),
    );
    this.add(this.glyphs);
  }

  resize(pitchLineGap: number, dpr: number) {
    const height = pitchLineGap * dpr * GLYPH_HEIGHT_IN_STAFF_GAPS;
    const width = height * GLYPH_WIDTH_TO_HEIGHT;
    const linePositions = this.getLinePositions();

    this.glyphs.forEach((glyph, index) => {
      glyph.resizeToHeight(height);
      glyph.setPosition(
        width / 2 + index * width * GLYPH_ADVANCE_FACTOR,
        (linePositions[index] / 2) * pitchLineGap * dpr,
      );
    });
    this.setSize(getKeySignatureWidth(this.fifths, pitchLineGap, dpr), pitchLineGap * dpr * 4);
  }

  setNotationColor(color: number) {
    this.notationColor = color;
    this.glyphs.forEach((glyph) => glyph.setNotationColor(this.notationColor));
  }

  private getLinePositions(): number[] {
    if (this.clef === 'treble') {
      return this.symbol === '#' ? TREBLE_SHARP_LINES : TREBLE_FLAT_LINES;
    }
    return this.symbol === '#' ? BASS_SHARP_LINES : BASS_FLAT_LINES;
  }

  destroy(fromScene?: boolean) {
    super.destroy(fromScene);
  }
}
